import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../Components/Button/Button';
import { getApi } from '../../../Services/apiService';
import { toast } from 'react-toastify';
import { CHECKR_STATUS, canStartBackgroundCheck, getCheckrStatusLabel } from '../../../Utils/checkrStatus';
import { BACKGROUND_CHECK_URL, BackgroundCheckPaymentForm, startBackgroundCheckPayment } from './BackgroundCheckPaymentForm';
import BackgroundCheckTermsConsent from './BackgroundCheckTermsConsent';

// What the tenant should know / do for each Checkr status
const STATUS_MESSAGES = {
  [CHECKR_STATUS.REQUESTED]: 'A landlord has requested a background check. Pay the background check fee to start it.',
  [CHECKR_STATUS.WAITING_FOR_APPLICANT]: 'Please complete your Checkr form. If you did not receive the email, use the link below.',
  [CHECKR_STATUS.PENDING]: 'Checkr is processing your background check. We will notify you once it is complete.',
  [CHECKR_STATUS.COMPLETED]: 'Your background check is complete and will be shared with landlords of properties you apply to.',
  [CHECKR_STATUS.CANCELED]: 'Your background check was canceled. You can start a new one below.',
};

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(Number(value) || value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const BackgroundCheck = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [check, setCheck] = useState(null);
  const [isStarting, setIsStarting] = useState(false);
  const [payment, setPayment] = useState(null); // { clientSecret, amount }
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [hasPaid, setHasPaid] = useState(false);

  const fetchBackgroundCheck = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await getApi(BACKGROUND_CHECK_URL);
      const data = res?.data;
      // Empty data (or a null status) means no background check has been performed
      setCheck(data && Object.keys(data).length > 0 && data.status ? data : null);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to fetch background check');
      setCheck(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBackgroundCheck();
  }, [fetchBackgroundCheck]);

  const handleStartBackgroundCheck = async () => {
    setIsStarting(true);
    try {
      const result = await startBackgroundCheckPayment();
      if (result.alreadyPaid) {
        toast.success(result.message || 'Your background check is already paid');
        setHasPaid(true);
        fetchBackgroundCheck();
        return;
      }
      setPayment({ clientSecret: result.clientSecret, amount: result.amount });
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Unable to start background check');
    } finally {
      setIsStarting(false);
    }
  };

  const handlePaymentSuccess = () => {
    setPayment(null);
    setHasAcceptedTerms(false);
    setHasPaid(true);
    fetchBackgroundCheck();
  };

  const renderContent = () => {
    if (isLoading) {
      return <p className="background-check-text">Loading background check...</p>;
    }

    if (payment) {
      return (
        <BackgroundCheckPaymentForm
          clientSecret={payment.clientSecret}
          amount={payment.amount}
          onPaymentSuccess={handlePaymentSuccess}
          onCancel={() => setPayment(null)}
        />
      );
    }

    // The status can lag behind the payment for a moment; don't offer the terms and Pay button again
    if (hasPaid && (canStartBackgroundCheck(check?.status) || check?.status === CHECKR_STATUS.REQUESTED)) {
      return <p className="background-check-text">Payment received. The background check link will be emailed to you shortly.</p>;
    }

    const startButton = (label) => (
      <>
        <BackgroundCheckTermsConsent checked={hasAcceptedTerms} onChange={setHasAcceptedTerms} />
        <div className="background-check-actions">
          <Button
            type="button"
            variant="success"
            onClick={handleStartBackgroundCheck}
            disabled={isStarting || !hasAcceptedTerms}
          >
            {isStarting ? 'Please wait...' : label}
          </Button>
        </div>
      </>
    );

    if (!check) {
      return (
        <>
          <p className="background-check-text">
            You have not completed a background check yet. Run one now and it will be shared with the landlords of properties you apply to.
          </p>
          {startButton('Check Background')}
        </>
      );
    }

    const { status } = check;

    return (
      <div className="background-check-details">
        <div className="background-check-row">
          <span className="background-check-label">Status</span>
          <span className={`background-check-status background-check-status-${status}`}>
            {getCheckrStatusLabel(status)}
            {status === CHECKR_STATUS.COMPLETED && (
              <button
                type="button"
                className="checkr-view-report-link"
                onClick={() => navigate('/tenant/background-check/report')}
              >
                (View)
              </button>
            )}
          </span>
        </div>
        {check.valid_until && status !== CHECKR_STATUS.CANCELED && (
          <div className="background-check-row">
            <span className="background-check-label">Valid Until</span>
            <span>{formatDate(check.valid_until)}</span>
          </div>
        )}
        {STATUS_MESSAGES[status] && <p className="background-check-text">{STATUS_MESSAGES[status]}</p>}
        {status === CHECKR_STATUS.REQUESTED && startButton('Pay for Background Check')}
        {status === CHECKR_STATUS.CANCELED && startButton('Check Background')}
        {status === CHECKR_STATUS.WAITING_FOR_APPLICANT && check.application_url && (
          <div className="background-check-actions">
            <Button
              type="button"
              variant="success"
              onClick={() => window.open(check.application_url, '_blank', 'noopener,noreferrer')}
            >
              Open Checkr Form
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="settings-password-card background-check-card">
      <h2 className="background-check-title">Background Check</h2>
      {renderContent()}
    </div>
  );
};

export default BackgroundCheck;
